import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { match, useHistory, useRouteMatch } from 'react-router-dom'
import { Divider } from '@fluentui/react-components'
import { useDispatch } from 'react-redux'

import Loading from '../../../components/Loading'
import useEntity from '../../../hooks/useEntity'
import { AppAction } from '../../../config/appConfig'
import { MenuItemKey } from '../../../components/MainMenu'
import { Route, Switch } from 'react-router'
import { usePageStyles } from '../File/ViewFile'
import ViewCRMHeader from './ViewCRMHeader'
import ViewCRMTabs, { CRMTabKey } from './ViewCRMTabs'
import CRMFileOverview from './Tabs/CRMFileOverview'
import CRMDocumentOverview from './Tabs/CRMDocumentOverview'
import CRMFinanceTab from './Tabs/CRMFinanceTab'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import { MutationRouteMatchProps } from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import { BasePartyDto, NaturalPersonDto } from '../../../config/partyConfig'
import { ToolbarItemKey } from '../File/ViewFileTabs'
import Tasks from '../../../pages/Tasks'
import AddFile from '../../../pages/File/AddFile'
import EditFile from '../../../pages/File/EditFile'
import EditNaturalPerson from '../../../pages/Party/EditNaturalPerson'
import EditLegalPerson from '../../../pages/Party/EditLegalPerson'

export const crmRoutePrefix = '/crm/:crmRelationId'

export type CRMRouteMatchProps = { crmRelationId: string; tab?: CRMTabKey } & MutationRouteMatchProps

export type ViewCRMProps = {
    crmRelationId: string
    routeMatch: match<CRMRouteMatchProps>

    crmRelation: BasePartyDto
    resetCRMRelation: () => void

    contacts: NaturalPersonDto[]
    resetContacts: () => void
}

export default function ViewCRM(): ReactElement {
    const routeMatch = useRouteMatch<CRMRouteMatchProps>()
    const crmRelationId = routeMatch.params?.crmRelationId
    const history = useHistory()

    const dispatch = useDispatch()
    const modules = useModules()
    const pageStyles = usePageStyles()

    // redirect to files tab when no tab was selected
    if (history?.location?.pathname === `/crm/${crmRelationId}`) {
        history.push(`/crm/${crmRelationId}/${CRMTabKey.Files}`)
    }

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.CRM] })
    }, [])

    const [crmRelation, , resetCRMRelation] = useEntity<BasePartyDto>(() => ({ url: `/party/${crmRelationId}/details` }), crmRelationId || false)
    const [contacts, , resetContacts] = useEntity<NaturalPersonDto[]>(() => ({ url: `/crm/${crmRelationId}/contacts` }), crmRelationId || false)

    useEffect(() => {
        resetCRMRelation()
        resetContacts()
    }, [history.location])

    const componentProps = useMemo<ViewCRMProps>(
        () => ({
            crmRelationId,
            routeMatch,
            crmRelation: crmRelation.data,
            resetCRMRelation,
            contacts: contacts.data,
            resetContacts,
        }),
        [routeMatch, crmRelation.loaded, contacts.loaded],
    )

    if (!crmRelation.loaded) {
        return <Loading />
    }

    return (
        <div className={pageStyles.page}>
            <ViewCRMHeader {...componentProps} />
            <ViewCRMTabs {...componentProps} />
            <Divider />
            <Switch>
                <Route path={`${crmRoutePrefix}/natural/:id/edit`} component={EditNaturalPerson} />
                <Route path={`${crmRoutePrefix}/legal/:id/edit`} component={EditLegalPerson} />

                <Route path={`${crmRoutePrefix}/${CRMTabKey.Files}`} render={() => <CRMFileOverview {...componentProps} />} />
                <Route path={`${crmRoutePrefix}/file/add`} component={AddFile} />
                <Route path={`${crmRoutePrefix}/file/edit`} component={EditFile} />

                <Route path={`${crmRoutePrefix}/${CRMTabKey.Documents}`} render={() => <CRMDocumentOverview {...componentProps} />} />

                {modules.has(ModuleType.Tasks) && (
                    <Route
                        path={`${crmRoutePrefix}/${ToolbarItemKey.Task}/:action?`}
                        render={() => <Tasks routePrefix={crmRoutePrefix} currentCRMRelation={crmRelation.data} />}
                    />
                )}
                {modules.has(ModuleType.Invoices) && (
                    <Route path={`${crmRoutePrefix}/${CRMTabKey.Finance}`} render={() => <CRMFinanceTab {...componentProps} />} />
                )}
            </Switch>
        </div>
    )
}
